import { useId } from "react";
import { Box, Typography, useTheme } from "@mui/material";
import { visuallyHidden } from "@mui/utils";
import dayjs from "dayjs";
import NotificationAvatar from "./NotificationAvatar";
import { ScreenerLogo } from "../Screener/ScreenerLogo";
import { focusRingSx } from "../Dashboard/overviewPanel";
import { isInsiderDetail, summariseInsider, type HeadlinePart } from "./insiderSummary";
import { NotificationItem, ScreenerMatchesDetail } from "./types";

// The inbox's own tile, a step up from the 36px default.
export const ROW_AVATAR_SIZE = 44;

// "2h", "3d" — a column of timestamps has to scan, and "2 hours ago" on every
// row pushes the headings around.
const formatNotificationTime = (createdAt: string): string => {
  const created = dayjs(createdAt);
  if (!created.isValid()) return "";
  const now = dayjs();
  const minutes = now.diff(created, "minute");
  if (minutes < 1) return "now";
  if (minutes < 60) return `${minutes}m`;
  const hours = now.diff(created, "hour");
  if (hours < 24) return `${hours}h`;
  const days = now.diff(created, "day");
  if (days < 7) return `${days}d`;
  if (created.year() === now.year()) return created.format("D MMM");
  return created.format("D MMM YYYY");
};

const clampLines = (lines: number) => ({
  display: "-webkit-box",
  WebkitLineClamp: lines,
  WebkitBoxOrient: "vertical" as const,
  overflow: "hidden",
});

// The title line's height, which the time and the unread dot share.
const TITLE_LINE = "20px";

// A saved screen's own footnote ("New on 12 Oct") repeats the row's time.
const SCREENER_TYPES = new Set(["screener.new_matches", "screener.screen_needs_update"]);

const isScreenerMatches = (detail: NotificationItem["detail"]): detail is ScreenerMatchesDetail =>
  detail?.kind === "screener_matches" && Array.isArray((detail as ScreenerMatchesDetail).matches);

interface TickerChipProps {
  symbol: string;
}

const TickerChip = ({ symbol }: TickerChipProps) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  return (
    <Box
      component="span"
      sx={{
        flexShrink: 0,
        px: 0.625,
        borderRadius: "4px",
        fontSize: "0.6875rem",
        fontWeight: 600,
        lineHeight: "16px",
        letterSpacing: "0.02em",
        color: theme.palette.text.secondary,
        backgroundColor: isDark ? "rgba(255, 255, 255, 0.08)" : "rgba(0, 0, 0, 0.05)",
      }}
    >
      {symbol}
    </Box>
  );
};

interface MatchLogosProps {
  detail: ScreenerMatchesDetail;
}

// The first few companies a screen picked up, largest first, ahead of their
// names.
const MatchLogos = ({ detail }: MatchLogosProps) => (
  <Box aria-hidden sx={{ display: "flex", gap: 0.375, flexShrink: 0, mr: 0.75 }}>
    {detail.matches.slice(0, 4).map((match, index) => (
      <ScreenerLogo key={`${match.symbol}-${index}`} symbol={match.symbol} name={match.name ?? undefined} size={20} />
    ))}
  </Box>
);

interface NotificationRowProps {
  item: NotificationItem;
  onOpen: (item: NotificationItem) => void;
  /** Side padding, in theme units, so the row's wash and rule run edge to edge. */
  inset: number;
}

/**
 * One notification in the inbox, two lines where it can be: what happened,
 * then the detail, with the time and unread dot on the right. An insider
 * alert takes a third line: the company, what its insiders did, then the
 * largest trade. Any unknown `type` or `kind` shows the heading and body the
 * server sent. The whole row is the only control — tap to open; nothing here
 * can be deleted.
 */
const NotificationRow = ({ item, onOpen, inset }: NotificationRowProps) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  // New: neither opened nor marked as seen, inside the 30-day window, as the
  // server counts it. "Mark all as seen" clears every dot without setting
  // readAt, so open rate still counts only real opens.
  const isUnread = item.recent && item.readAt === null && item.clearedAt === null;
  const factsId = useId();
  const footnoteId = useId();
  const timeId = useId();
  const created = dayjs(item.createdAt);

  const insider = isInsiderDetail(item.detail) ? item.detail : null;
  const summary = insider ? summariseInsider(insider) : null;
  const matches = isScreenerMatches(item.detail) ? item.detail : null;
  const company = insider?.companyName ?? null;
  const ticker = item.imageKind === "symbol" ? item.imageValue : null;
  const headline: HeadlinePart[] = summary?.headline ?? [{ text: item.heading, tone: "plain" }];
  const headlineText = headline.map((part) => part.text).join("");
  const facts = summary?.facts ?? item.body;
  // The company is the row's title, so a subtext that opens with it drops it.
  const subtext =
    company && item.subtext?.startsWith(`${company} · `) ? item.subtext.slice(company.length + 3) : item.subtext;
  const footnote = summary ? summary.footnote : SCREENER_TYPES.has(item.type) ? null : subtext;
  const subject = company && `${company}${ticker ? ` (${ticker})` : ""}: `;
  const toneColor = (tone: HeadlinePart["tone"]) =>
    (tone === "buy" && theme.palette.success.main) || (tone === "sell" && theme.palette.error.main) || "inherit";

  const titleSx = {
    minWidth: 0,
    fontSize: "0.875rem",
    fontWeight: 600,
    lineHeight: TITLE_LINE,
    letterSpacing: "-0.005em",
    color: theme.palette.text.primary,
    fontVariantNumeric: "tabular-nums",
  };
  const headlineParts = headline.map((part, index) => (
    <Box key={index} component="span" sx={{ color: toneColor(part.tone) }}>
      {part.text}
    </Box>
  ));

  return (
    <Box
      role="button"
      tabIndex={0}
      aria-label={`${isUnread ? "Unread: " : ""}${subject ?? ""}${headlineText}`}
      aria-describedby={
        [facts ? factsId : null, footnote ? footnoteId : null, created.isValid() ? timeId : null]
          .filter(Boolean)
          .join(" ") || undefined
      }
      onClick={() => onOpen(item)}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onOpen(item);
        }
      }}
      sx={{
        display: "grid",
        gridTemplateColumns: `${ROW_AVATAR_SIZE}px minmax(0, 1fr) auto`,
        columnGap: 1.5,
        // A row with one line of text sits on its picture's centre line.
        alignItems: facts || footnote ? "start" : "center",
        px: inset,
        py: 1.5,
        cursor: "pointer",
        WebkitTapHighlightColor: "transparent",
        transition: "background-color 0.12s ease",
        "@media (hover: hover)": {
          "&:hover": {
            backgroundColor: isDark ? "rgba(255, 255, 255, 0.035)" : "rgba(0, 0, 0, 0.025)",
          },
        },
        "&:active": {
          backgroundColor: isDark ? "rgba(255, 255, 255, 0.06)" : "rgba(0, 0, 0, 0.045)",
        },
        ...focusRingSx,
        // Inset: the rows run edge to edge, where an outside ring is clipped.
        "&:focus-visible": { ...focusRingSx["&:focus-visible"], outlineOffset: "-2px" },
        "@media (prefers-reduced-motion: reduce)": { transition: "none" },
      }}
    >
      <NotificationAvatar
        imageKind={item.imageKind}
        imageValue={item.imageValue}
        name={company ?? undefined}
        branded={item.type === "publication.published"}
        size={ROW_AVATAR_SIZE}
      />

      <Box sx={{ minWidth: 0 }}>
        {company && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.75,
              minWidth: 0,
              height: TITLE_LINE,
            }}
          >
            <Typography
              component="span"
              sx={{
                ...titleSx,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {company}
            </Typography>
            {ticker && <TickerChip symbol={ticker} />}
          </Box>
        )}

        <Typography
          component="p"
          sx={{
            ...titleSx,
            ...clampLines(company ? 1 : 2),
            ...(company && {
              fontSize: "0.8125rem",
              fontWeight: 500,
              lineHeight: "19px",
            }),
          }}
        >
          {headlineParts}
        </Typography>

        {(facts || footnote) && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              minWidth: 0,
              mt: matches ? 0.5 : 0.25,
            }}
          >
            {matches && <MatchLogos detail={matches} />}
            <Typography
              sx={{
                minWidth: 0,
                fontSize: "0.8125rem",
                lineHeight: "19px",
                color: theme.palette.text.secondary,
                fontVariantNumeric: "tabular-nums",
                ...clampLines(matches || summary ? 1 : 2),
              }}
            >
              {facts && <span id={factsId}>{facts}</span>}
              {facts && footnote && " · "}
              {footnote && <span id={footnoteId}>{footnote}</span>}
            </Typography>
          </Box>
        )}
      </Box>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.75,
          height: TITLE_LINE,
        }}
      >
        {isUnread && (
          <Box
            aria-hidden
            sx={{
              width: 7,
              height: 7,
              borderRadius: "50%",
              backgroundColor: theme.palette.primary.main,
            }}
          />
        )}
        <Typography
          component="time"
          dateTime={item.createdAt}
          title={created.isValid() ? created.format("D MMM YYYY, HH:mm") : undefined}
          sx={{
            fontSize: "0.75rem",
            lineHeight: TITLE_LINE,
            fontVariantNumeric: "tabular-nums",
            color: isUnread ? theme.palette.text.primary : theme.palette.text.secondary,
            fontWeight: isUnread ? 600 : 400,
          }}
        >
          {formatNotificationTime(item.createdAt)}
        </Typography>
        {/* The full date for screen readers, where "5h" says little. */}
        {created.isValid() && (
          <Box component="span" id={timeId} sx={visuallyHidden}>
            {created.format("D MMM YYYY, HH:mm")}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default NotificationRow;
